import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { useCart } from "@/context/CartContext";
import { useCustomer } from "@/context/CustomerContext";
import { toast } from "sonner";
import { Refrigerator, Clock, ArrowLeft, Package, Phone } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ProductDetailPage = () => {
  const { productId } = useParams();
  const navigate = useNavigate();
  const { addToCart, cart } = useCart();
  const { customer, isLoggedIn } = useCustomer();
  const [product, setProduct] = useState(null);
  const [category, setCategory] = useState(null);
  const [loading, setLoading] = useState(true);

  const isBusiness = isLoggedIn && customer?.account_type === "business" && !customer?.partner_company_id;

  // Ottieni quantità nel carrello
  const getCartQuantity = () => {
    const cartItem = cart.find(item => item.id === productId);
    return cartItem ? cartItem.quantity : 0;
  };

  useEffect(() => {
    fetchProduct();
  }, [productId, isBusiness]);

  const fetchProduct = async () => {
    try {
      const response = await axios.get(`${API}/products/${productId}`, {
        params: { is_business_customer: isBusiness }
      });
      setProduct(response.data);
      
      // Fetch category name
      const categoriesResponse = await axios.get(`${API}/categories`);
      const productCategory = categoriesResponse.data.find(c => c.id === response.data.category_id);
      setCategory(productCategory);
    } catch (error) {
      console.error("Errore nel caricamento del prodotto:", error);
      toast.error("Prodotto non trovato");
      navigate("/catalogo");
    } finally {
      setLoading(false);
    }
  };

  const getDurationText = () => {
    if (category?.name === "Dolci" || category?.name === "Tramezzini Gourmet") {
      return "Scadenza garantita: non meno di 8 giorni";
    }
    return "Scadenza garantita: non meno di 12 giorni";
  };

  const handleAddToCart = () => {
    const currentInCart = getCartQuantity();
    const stock = product.stock || 0;
    
    if (currentInCart >= stock) {
      toast.error(`Disponibilità insufficiente! Solo ${stock} pezzi in magazzino.`);
      return;
    }
    
    addToCart(product);
    toast.success(`${product.name} aggiunto al carrello`);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex flex-col">
        <Header />
        <div className="flex-1 flex items-center justify-center" data-testid="loading-spinner">
          <div className="inline-block w-12 h-12 border-4 border-green-500 border-t-transparent rounded-full animate-spin"></div>
        </div>
        <Footer />
      </div>
    );
  }

  if (!product) return null;

  const stock = product.stock || 0;
  const isOutOfStock = stock <= 0 || product.sold_out;
  const cartQuantity = getCartQuantity();
  const remainingStock = stock - cartQuantity;
  const isGastronomia = product.business_only === true;

  return (
    <div className="min-h-screen flex flex-col">
      <Header />
      
      <main className="flex-1 max-w-4xl mx-auto px-4 py-8" data-testid="product-detail-page">
        <button
          onClick={() => navigate(-1)}
          className="flex items-center gap-2 text-zinc-400 hover:text-white mb-6 transition-colors"
          data-testid="back-button"
        >
          <ArrowLeft className="w-5 h-5" />
          Indietro
        </button>

        {/* Product Info */}
        <div className="flex flex-col">
          {/* Badges */}
          <div className="flex flex-wrap gap-2 mb-4">
            <span className="bg-green-500 text-black text-sm font-semibold px-4 py-2 rounded-full inline-flex items-center gap-2"
              data-testid="fresh-badge">
              <Refrigerator className="w-4 h-4" />
              Fresco da frigo
            </span>
            {isOutOfStock ? (
              <span className="bg-red-500 text-white text-sm font-semibold px-4 py-2 rounded-full"
                data-testid="sold-out-badge">
                Esaurito
              </span>
            ) : stock <= 5 ? (
              <span className="bg-yellow-500 text-black text-sm font-semibold px-4 py-2 rounded-full"
                data-testid="low-stock-badge">
                Ultimi {stock} pezzi!
              </span>
            ) : (
              <span className="bg-zinc-700 text-white text-sm font-semibold px-4 py-2 rounded-full inline-flex items-center gap-2"
                data-testid="stock-badge">
                <Package className="w-4 h-4" />
                {stock} disponibili
              </span>
            )}
          </div>
          
          <h1 className="text-4xl font-bold text-white mb-4" data-testid="product-name">{product.name}</h1>
            
            {/* Short description */}
            <p className="text-green-500 text-lg font-semibold mb-4">
              {product.short_description}
            </p>
            
            {/* Full description */}
            <div className="bg-zinc-900 border border-zinc-800 rounded-lg p-6 mb-6">
              <h2 className="text-xl font-bold text-white mb-3">Descrizione</h2>
              <p className="text-zinc-300 text-base leading-relaxed" data-testid="product-full-description">
                {product.full_description}
              </p>
            </div>

            {/* Duration info */}
            {product.nutrition_kcal != null && (
              <div className="bg-zinc-900 border border-zinc-800 rounded-lg p-6 mb-6" data-testid="nutrition-table">
                <h2 className="text-xl font-bold text-white mb-3">Valori Nutrizionali per 100g</h2>
                <div className="divide-y divide-zinc-800">
                  <div className="flex justify-between py-2"><span className="text-zinc-400">Energia</span><span className="text-white font-semibold">{product.nutrition_kcal} kcal</span></div>
                  {product.nutrition_grassi != null && <div className="flex justify-between py-2"><span className="text-zinc-400">Grassi</span><span className="text-white font-semibold">{product.nutrition_grassi} g</span></div>}
                  {product.nutrition_grassi_saturi != null && <div className="flex justify-between py-2 pl-4"><span className="text-zinc-500">di cui Saturi</span><span className="text-zinc-300">{product.nutrition_grassi_saturi} g</span></div>}
                  {product.nutrition_carboidrati != null && <div className="flex justify-between py-2"><span className="text-zinc-400">Carboidrati</span><span className="text-white font-semibold">{product.nutrition_carboidrati} g</span></div>}
                  {product.nutrition_zuccheri != null && <div className="flex justify-between py-2 pl-4"><span className="text-zinc-500">di cui Zuccheri</span><span className="text-zinc-300">{product.nutrition_zuccheri} g</span></div>}
                  {product.nutrition_proteine != null && <div className="flex justify-between py-2"><span className="text-zinc-400">Proteine</span><span className="text-white font-semibold">{product.nutrition_proteine} g</span></div>}
                  {product.nutrition_sale != null && <div className="flex justify-between py-2"><span className="text-zinc-400">Sale</span><span className="text-white font-semibold">{product.nutrition_sale} g</span></div>}
                </div>
              </div>
            )}

            {/* Duration info */}
            <div className="bg-zinc-900 border border-zinc-800 rounded-lg p-6 mb-6">
              <div className="flex items-start gap-3 mb-4">
                <Clock className="w-5 h-5 text-green-500 mt-1" />
                <div>
                  <p className="text-white font-semibold mb-2" data-testid="product-duration">
                    {getDurationText()}
                  </p>
                  <p className="text-zinc-400 text-sm" data-testid="storage-note">
                    Conservare in frigorifero a +4°C
                  </p>
                  <p className="text-zinc-400 text-sm mt-2">
                    ✓ Fresco da frigo<br/>
                    ✓ Confezionato in modo sicuro
                    {category?.name !== "Dolci" && category?.name !== "Tramezzini Gourmet" && (
                      <><br/>✓ Pronto da riscaldare</>
                    )}
                  </p>
                </div>
              </div>
            </div>

            <div className="flex items-center justify-between mb-6">
              <span className="text-5xl font-bold text-white" data-testid="product-price">
                €{product.price.toFixed(2)}
              </span>
              {cartQuantity > 0 && (
                <span className="text-zinc-400 text-sm">
                  {cartQuantity} nel carrello
                </span>
              )}
            </div>

            {remainingStock <= 0 && !isOutOfStock && !isGastronomia && (
              <div className="bg-yellow-500/10 border border-yellow-500 rounded-lg p-4 mb-4">
                <p className="text-yellow-500 text-sm font-semibold">
                  Hai già aggiunto tutti i {stock} pezzi disponibili al carrello!
                </p>
              </div>
            )}

            {isGastronomia ? (
              <div className="bg-yellow-500/10 border border-yellow-500 rounded-lg p-6 text-center">
                <Phone className="w-12 h-12 text-yellow-500 mx-auto mb-4" />
                <h3 className="text-xl font-bold text-yellow-500 mb-2">Disponibile su ordinazione</h3>
                <p className="text-zinc-300 mb-4">
                  Questo prodotto del formato gastronomia non è acquistabile online.
                  Contattaci per effettuare l'ordine.
                </p>
                <a 
                  href="/contatti" 
                  className="inline-block bg-yellow-500 text-black px-8 py-3 rounded-lg font-bold hover:bg-yellow-600 transition-all"
                >
                  Contattaci per ordinare
                </a>
              </div>
            ) : (
              <button
                onClick={handleAddToCart}
                disabled={isOutOfStock || remainingStock <= 0}
                className={`w-full py-4 rounded-lg font-bold text-lg transition-all ${
                  isOutOfStock || remainingStock <= 0
                    ? "bg-zinc-800 text-zinc-600 cursor-not-allowed"
                    : "bg-green-500 text-black hover:bg-green-600"
                }`}
                data-testid="add-to-cart-button"
              >
                {isOutOfStock ? "Esaurito" : remainingStock <= 0 ? "Massimo raggiunto" : "Aggiungi al carrello"}
              </button>
            )}
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default ProductDetailPage;
